import { type RefObject, useEffect, useState } from 'react';
import {
  EnrichedTextInput,
  type EnrichedTextInputInstance,
  type HtmlStyle,
  type MentionStyleProperties,
} from 'react-native-enriched-html';
import { KeyboardEvents } from 'react-native-keyboard-controller';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import { richTextSystemFontFamily } from '@/components/RichText/RichText.utils';
import Box from '@/components/Unistyles/Box';
import isDesktopUserAgent from '@/utils/isDesktopUserAgent';

const ThemedEnrichedTextInput = withUnistyles(EnrichedTextInput, (theme) => ({
  htmlStyle: {
    a: {
      color: theme.colors.primary,
      textDecorationLine: 'none',
    } satisfies HtmlStyle['a'],
    mention: {
      backgroundColor: theme.colors.surfaceSecondary,
      color: theme.colors.info,
      textDecorationLine: 'none',
    } satisfies MentionStyleProperties,
  },
  placeholderTextColor: theme.colors.typographySecondary,
}));

type ChatInputTextProps = {
  defaultValue: string;
  inputRef: RefObject<EnrichedTextInputInstance | null>;
  mentionSuggestionsVisible: boolean;
  selectedMentionPersonId: string | null;
  onMentionKeyPress: (key: string) => boolean;
  onMentionSearchChange: (value: string | null) => void;
  onMentionSubmit: () => boolean;
  onChangeText: (value: string) => void;
  onChangeHtml: (value: string) => void;
  onFocusChange: (isFocused: boolean) => void;
  onSubmit: () => void;
};

export default function ChatInputText({
  defaultValue,
  inputRef,
  mentionSuggestionsVisible,
  selectedMentionPersonId,
  onMentionKeyPress,
  onMentionSearchChange,
  onMentionSubmit,
  onChangeText,
  onChangeHtml,
  onFocusChange,
  onSubmit,
}: ChatInputTextProps) {
  const [initialDefaultValue] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (process.env.EXPO_OS !== 'android' || !isFocused) return;

    const subscription = KeyboardEvents.addListener('keyboardDidHide', () => {
      inputRef.current?.blur();
    });

    return () => subscription.remove();
  }, [inputRef, isFocused]);

  return (
    <Box style={styles.wrapper(isFocused)}>
      <ThemedEnrichedTextInput
        ref={inputRef}
        accessibilityLabel="Message input"
        role={process.env.EXPO_OS === 'web' ? 'combobox' : undefined}
        aria-autocomplete={process.env.EXPO_OS === 'web' ? 'list' : undefined}
        aria-expanded={mentionSuggestionsVisible}
        aria-controls={
          mentionSuggestionsVisible ? 'ChatMentionSuggestions' : undefined
        }
        aria-activedescendant={
          mentionSuggestionsVisible && selectedMentionPersonId
            ? `ChatMentionSuggestionOption-${selectedMentionPersonId}`
            : undefined
        }
        defaultValue={initialDefaultValue}
        nativeID="composer"
        onBlur={() => {
          setIsFocused(false);
          onFocusChange(false);
        }}
        onStartMention={() => onMentionSearchChange('')}
        onChangeMention={({ text }) => onMentionSearchChange(text)}
        onEndMention={() => onMentionSearchChange(null)}
        onChangeHtml={(event) => onChangeHtml(event.nativeEvent.value)}
        onChangeText={(event) => onChangeText(event.nativeEvent.value)}
        onFocus={() => {
          setIsFocused(true);
          onFocusChange(true);
        }}
        onKeyPress={(event) => {
          if (onMentionKeyPress(event.nativeEvent.key)) event.preventDefault();
        }}
        onSubmitEditing={() => {
          if (!onMentionSubmit()) onSubmit();
        }}
        submitBehavior={isDesktopUserAgent ? 'submit' : 'newline'}
        placeholder="Type Message…"
        style={styles.input}
        testID="ChatInput"
      />
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  wrapper: (isFocused: boolean) => ({
    width: '100%',
    borderColor: isFocused ? theme.colors.primary : theme.colors.borderMuted,
    borderRadius: theme.borderRadius['2xl'],
    borderWidth: theme.size(0.5),
    _web: {
      outline: isFocused
        ? `${theme.size(0.25)}px solid ${theme.colors.primary}`
        : 'none',
      outlineOffset: 0,
    },
  }),
  input: {
    borderWidth: 0,
    color: theme.colors.typography,
    fontFamily: richTextSystemFontFamily,
    fontSize: theme.fontSize.lg,
    maxHeight: theme.size(50),
    paddingHorizontal: theme.size(3),
    paddingVertical: theme.space('md'),
    textAlignVertical: 'top',
    width: '100%',
  },
}));
